import { useNavigate } from "@tanstack/react-router"
import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

/**
 * CoachMessagesInbox - Unified inbox for all coach messages
 *
 * Fetches recent messages from ALL coaches and displays them in a unified feed.
 * Messages are pulled from existing coach_messages table.
 */

interface CoachMessage {
  id: string
  conversationId: string
  coachId: string
  coachName: string
  coachIcon: string
  content: string
  timestamp: string | Date
  isRead: boolean
  messageType: "pattern" | "accountability" | "celebration" | "suggestion" | "reminder" | "warning"
}

function formatTimestamp(timestamp: string | Date): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) {
    return diffMins <= 1 ? "just now" : `${diffMins} minutes ago`
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`
  } else if (diffDays < 7) {
    return diffDays === 1 ? "yesterday" : `${diffDays} days ago`
  } else {
    return date.toLocaleDateString()
  }
}

const messageTypeStyles: Record<CoachMessage["messageType"], { bg: string; border: string; icon: string }> = {
  pattern: { bg: "bg-primary/10", border: "border-primary", icon: "⚡" },
  accountability: { bg: "bg-destructive/10", border: "border-destructive", icon: "⚠️" },
  celebration: { bg: "bg-green-50", border: "border-green-500", icon: "🎉" },
  suggestion: { bg: "bg-primary/10", border: "border-primary", icon: "💡" },
  reminder: { bg: "bg-primary/10", border: "border-primary", icon: "🔔" },
  warning: { bg: "bg-destructive/10", border: "border-destructive", icon: "⚠️" },
}

function MessageCard({ message }: { message: CoachMessage }) {
  const navigate = useNavigate()
  const styles = messageTypeStyles[message.messageType]

  return (
    <div
      className={`border-2 ${styles.border} ${styles.bg} rounded-lg p-4 transition-all hover:shadow-md cursor-pointer ${
        !message.isRead ? "font-medium" : ""
      }`}
      onClick={() =>
        navigate({
          to: "/coach-os/coach/$coachId",
          params: { coachId: message.coachId },
          // @ts-ignore
          search: { tab: "chat" },
        })
      }
    >
      <div className="space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!message.isRead && <span className="text-destructive text-xs font-bold">🔴 NEW</span>}
            <span className="text-lg">{message.coachIcon}</span>
            <span className="font-bold text-sm text-foreground">{message.coachName} Coach</span>
          </div>
          <span className="text-xs text-muted-foreground">{formatTimestamp(message.timestamp)}</span>
        </div>

        {/* Message content */}
        <p className="text-sm text-foreground leading-relaxed">{message.content}</p>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="outline" className="text-xs">
            Reply
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={(e) => {
              e.stopPropagation()
              navigate({
                to: "/coach-os/coach/$coachId",
                params: { coachId: message.coachId },
                // @ts-ignore
                search: { tab: "chat" },
              })
            }}
          >
            View Full Chat
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function CoachMessagesInbox() {
  const navigate = useNavigate()

  // Fetch recent messages from all coaches
  const { data, isLoading } = trpc.coachDashboard.getRecentMessages.useQuery({
    limit: 10,
    includeRead: false,
  })

  const messages = data?.messages || []
  const unreadCount = messages.filter((m) => !m.isRead).length

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">💬 Coach Messages</h2>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="border-2 border-border rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-muted rounded mb-2 w-1/3"></div>
              <div className="h-3 bg-muted rounded mb-1 w-full"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">💬 Coach Messages</h2>
        <div className="border-2 border-border rounded-lg p-6 text-center bg-muted/30">
          <p className="text-sm text-muted-foreground">
            No messages yet. Your coaches will send you insights and accountability nudges here.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">
          💬 Coach Messages {unreadCount > 0 && `(${unreadCount} unread)`}
        </h2>
        <Button
          onClick={() =>
            navigate({
              to: "/coach-os",
              // @ts-ignore
              search: (prev) => ({ ...prev, tab: "messages" }),
            })
          }
          variant="ghost"
          size="sm"
          className="text-xs"
        >
          View All Messages →
        </Button>
      </div>

      <div className="space-y-3">
        {messages.slice(0, 2).map((message) => (
          <MessageCard key={message.id} message={message} />
        ))}
      </div>

      {messages.length > 2 && (
        <div className="text-center">
          <Button
            onClick={() =>
              navigate({
                to: "/coach-os",
                // @ts-ignore
                search: (prev) => ({ ...prev, tab: "messages" }),
              })
            }
            variant="outline"
            size="sm"
          >
            View All Messages ({messages.length - 2} more)
          </Button>
        </div>
      )}
    </div>
  )
}
